import React, {
    ChangeEvent,
    Dispatch,
    Fragment,
    SetStateAction,
    useRef,
    useState
} from 'react';
import clsx from 'clsx';
import { Listbox, Transition } from '@headlessui/react';
import {
    CheckIcon,
    ChevronDownIcon,
    ChevronUpIcon
} from '@heroicons/react/24/solid';
import isEmpty from 'lodash/isEmpty';
import { ScrollToFormikError } from '../elements/ScrollToFormikError';

export interface ISelectDropdownWithImageItem {
    id?: string;
    name?: string;
    value: string | number | boolean | null;
    imageSrc?: string;
}

export interface ISelectDropdownWithImageProps {
    selectInputClassName?: string;
    label?: string;
    listSelectedValue?: number | string | boolean | null;
    search?: boolean;
    onChange: Dispatch<SetStateAction<ISelectDropdownWithImageItem | null>> | any;
    onBlur?: (e: React.FocusEvent<any, Element>) => void | null | any;
    list?: Array<ISelectDropdownWithImageItem>;
    placeholder?: string;
    description?: JSX.Element | React.ReactElement | string | number | any;
    name?: string;
    error?: string | undefined;
    disabled?: boolean;
    required?: boolean;
    helperText?: string | undefined;
    emptyListMessage?: string;
    searchPlaceHolder?: string;
    wrapperClasses?: string;
    editable?: boolean;
    imageOnly?: boolean;
    showDropdownIcon?: boolean;
}

function SelectDropdownWithImage<T>({
    selectInputClassName,
    label,
    listSelectedValue,
    onChange,
    list = [],
    description,
    name,
    error,
    disabled = false,
    required = false,
    helperText,
    onBlur,
    searchPlaceHolder = 'Search ...',
    wrapperClasses,
    editable = true,
    placeholder,
    emptyListMessage,
    search,
    imageOnly = false,
    showDropdownIcon = true
}: ISelectDropdownWithImageProps): JSX.Element {
    const [searchValue, setSearchValue] = useState<string>('');
    const [showDropdown, setShowDropdown] = useState<boolean>(false);

    const fieldRef = useRef<HTMLDivElement>(null);

    const [searchResult, setSearchResult] = useState<Array<ISelectDropdownWithImageItem>>([]);
    const searchedList = isEmpty(searchResult) && searchValue.length < 1 ? list : searchResult;

    const onSearchChange = (evt: ChangeEvent<HTMLInputElement> | undefined) => {
        const val = evt?.target.value ?? '';
        setSearchValue(val);
        const srchList: Array<ISelectDropdownWithImageItem> = [];

        list.forEach((listItem) => {
            if (listItem.name?.toString().toLowerCase().includes(val)) srchList.push(listItem);
        });
        setSearchResult(srchList);
    };

    const getListItemDisplay = () => {
        const selectedItem = list.find(
            (listItem: ISelectDropdownWithImageItem) => listItem.value === listSelectedValue
        );
        if (!selectedItem) return placeholder || 'Select Option';

        return selectedItem.imageSrc ? (
            <span className="flex items-center">
                <img src={selectedItem.imageSrc} alt={selectedItem.name || 'Selected'} className="w-6 h-6 mr-2" />
                {selectedItem.name && !imageOnly && <span>{selectedItem.name}</span>}
            </span>
        ) : (
            selectedItem.name || 'No Name'
        );
    };

    return (
        <Listbox
            ref={fieldRef}
            value={list.find((listItem: ISelectDropdownWithImageItem) => listItem.value === listSelectedValue)?.value}
            onChange={onChange}
            disabled={disabled}
            as={'div'}
            className={clsx({ ['animate-wiggle']: error && required }, wrapperClasses)}>
            {({ open }) => (
                <>
                    {label && (
                        <Listbox.Label className={clsx('block text-sm font-medium text-gray-700', { ['!text-danger-main']: error && required })}>
                            {label} <span className="!text-danger-main">{required && '*'}</span>
                            {description && <p className="text-xs text-gray-400 italic">{description}</p>}
                        </Listbox.Label>
                    )}

                    <div className="mt-1 relative">
                        <Listbox.Button className={clsx('relative w-full bg-white rounded-full border border-gray-300 pl-3 pr-6 py-1 text-left cursor-pointer text-xs', selectInputClassName)}>
                            {getListItemDisplay()}
                            {showDropdownIcon &&
                                <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    {open ? <ChevronUpIcon className="h-5 w-5 text-gray-400" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                                </span>}
                        </Listbox.Button>

                        <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                            <Listbox.Options className="absolute z-[9999px] mt-1 w-full bg-white shadow-lg max-h-96 rounded-lg py-1 overflow-auto">
                                {isEmpty(list) ? (
                                    <div className="w-full flex justify-center items-center h-20 flex-col">
                                        <h4 className="font-medium text-red-400 px-1 text-sm">No Options Available!</h4>
                                        <small className="text-xs text-gray-500">{emptyListMessage || 'Refresh to try again'}</small>
                                    </div>
                                ) : (
                                    searchedList.map((listItem, key) => (
                                        <Listbox.Option key={key} className="cursor-pointer select-none relative py-2 px-3 pr-0 text-sm" value={listItem.value}>
                                            {({ selected }) => (
                                                <div className={`${!imageOnly && 'gap-x-2 flex items-center '}`}>
                                                    {listItem.imageSrc && !imageOnly && <img src={listItem.imageSrc} alt={listItem.name || 'Option'} className="w-6 h-6 bg-contain" />}
                                                    {listItem.imageSrc && imageOnly && <img src={listItem.imageSrc} alt={listItem.name || 'Option'} className="w-auto h-6 bg-fill" />}
                                                    {!listItem.imageSrc && <span className='h-6 w-6'></span>}
                                                    {listItem.name && !imageOnly && <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>{listItem.name}</span>}
                                                    {/* {selected && <CheckIcon className="h-5 w-5 text-primary-main" />} */}
                                                </div>
                                            )}
                                        </Listbox.Option>
                                    ))
                                )}
                            </Listbox.Options>
                        </Transition>
                    </div>
                    <ScrollToFormikError fieldName={name ?? ''} fieldRef={fieldRef} />
                </>
            )
            }
        </Listbox >
    );
}

export default SelectDropdownWithImage;
